import { useQuery } from '@tanstack/react-query';
import { UsersService } from '../services';
import { usersKeys } from '../queryKeys';
import { UNKNOWN_ROLE_LABEL } from '../internalRoles';

/**
 * The signed-in admin's own GoTu Admin role, for display.
 *
 * Takes no arguments: auth-service resolves the caller from the token, so there
 * is nothing to key on but the session itself.
 *
 * `retry: false` because the failure this is most likely to see is a 404 — an
 * admin with no `internal_user` row — which is a settled answer, not a blip.
 * Retrying it three times would delay the fallback for no gain.
 *
 * `roleLabel` is null until the query settles, so the caller can leave the line
 * out rather than render a placeholder that immediately changes. On any
 * failure it resolves to UNKNOWN_ROLE_LABEL, so a caller that renders it
 * unconditionally after settling cannot produce a blank role.
 */
export const useGetInternalUserMe = () => {
  const { data, error, isError, isPending, isFetching } = useQuery({
    queryKey: usersKeys.internalUserMe(),
    queryFn: () => UsersService.getInternalUserMe(),
    retry: false,
  });

  const settled = !isPending;

  return {
    role: data?.role ?? null,
    roleLabel: (settled && (data?.roleLabel ?? UNKNOWN_ROLE_LABEL)) || null,
    isPending,
    isFetching,
    isError,
    error,
  };
};
